/* DUHN: lightweight web components for custom sections. No dependencies. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/* Calls `callback` once per animation frame while the page scrolls or resizes. */
const scrollSubscribers = new Set();
let scrollTicking = false;
function onScrollFrame() {
  scrollTicking = false;
  for (const callback of scrollSubscribers) callback();
}
function requestScrollFrame() {
  if (scrollTicking) return;
  scrollTicking = true;
  requestAnimationFrame(onScrollFrame);
}
// Capture phase: Horizon scrolls `.page-wrapper` (not the window) on desktop.
document.addEventListener('scroll', requestScrollFrame, { passive: true, capture: true });
window.addEventListener('resize', requestScrollFrame);

/* ---------- Carousel ---------- */

class DuhnCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.bar = this.querySelector('[data-progress]');

    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.onScroll = () => requestAnimationFrame(() => this.update());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  go(direction) {
    const item = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
    const perView = Math.max(1, Math.round(this.track.clientWidth / step));
    this.track.scrollBy({ left: direction * step * perView, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = Math.max(0, scrollWidth - clientWidth);
    if (this.prev) this.prev.disabled = scrollLeft <= 2;
    if (this.next) this.next.disabled = scrollLeft >= max - 2;
    if (this.bar) {
      const size = scrollWidth ? (clientWidth / scrollWidth) * 100 : 100;
      const ratio = max ? scrollLeft / max : 0;
      this.bar.style.setProperty('--progress-size', `${size}%`);
      this.bar.style.setProperty('--progress-x', `${ratio * (100 / size - 1) * 100}%`);
      this.bar.hidden = size >= 99.5;
    }
  }
}

/* ---------- Hero: scroll-linked parallax ---------- */

class DuhnHero extends HTMLElement {
  connectedCallback() {
    if (reduceMotion.matches) return;
    this.update = () => {
      const rect = this.getBoundingClientRect();
      const progress = clamp(-rect.top / rect.height, 0, 1);
      this.style.setProperty('--p', progress.toFixed(4));
    };
    scrollSubscribers.add(this.update);
    this.update();
    if (finePointer.matches) {
      this.addEventListener('pointermove', (e) => {
        const r = this.getBoundingClientRect();
        this.style.setProperty('--mx', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
        this.style.setProperty('--my', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
      }, { passive: true });
      this.addEventListener('pointerleave', () => { this.style.setProperty('--mx', 0); this.style.setProperty('--my', 0); });
    }
  }

  disconnectedCallback() {
    scrollSubscribers.delete(this.update);
  }
}

/* ---------- Manifesto: words light up as the section scrolls past ---------- */

class DuhnManifesto extends HTMLElement {
  connectedCallback() {
    this.words = [...this.querySelectorAll('[data-word]')];
    if (!this.words.length) return;
    if (reduceMotion.matches || document.documentElement.classList.contains('shopify-design-mode')) {
      this.words.forEach((word) => word.classList.add('is-lit'));
      return;
    }
    this.update = () => {
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight;
      const progress = clamp((viewport * 0.85 - rect.top) / (rect.height + viewport * 0.2), 0, 1);
      const lit = Math.round(progress * this.words.length);
      this.words.forEach((word, i) => word.classList.toggle('is-lit', i < lit));
    };
    scrollSubscribers.add(this.update);
    this.update();
  }

  disconnectedCallback() {
    scrollSubscribers.delete(this.update);
  }
}

/* ---------- Category index: image preview follows the cursor ---------- */

class DuhnIndex extends HTMLElement {
  connectedCallback() {
    this.preview = this.querySelector('[data-preview]');
    this.images = [...this.querySelectorAll('[data-preview-image]')];
    this.rows = [...this.querySelectorAll('[data-row]')];
    if (!this.preview || !finePointer.matches) return;

    this.target = { x: 0, y: 0 };
    this.current = { x: 0, y: 0 };
    this.onMove = (event) => {
      const rect = this.getBoundingClientRect();
      this.target.x = event.clientX - rect.left;
      this.target.y = event.clientY - rect.top;
      if (!this.raf) this.raf = requestAnimationFrame(this.tick);
    };
    this.tick = () => {
      const ease = reduceMotion.matches ? 1 : 0.14;
      this.current.x += (this.target.x - this.current.x) * ease;
      this.current.y += (this.target.y - this.current.y) * ease;
      const skew = clamp((this.target.x - this.current.x) * 0.04, -8, 8);
      this.preview.style.transform = `translate3d(${this.current.x}px, ${this.current.y}px, 0) rotate(${skew}deg)`;
      const settled = Math.abs(this.target.x - this.current.x) < 0.5 && Math.abs(this.target.y - this.current.y) < 0.5;
      this.raf = settled ? null : requestAnimationFrame(this.tick);
    };
    this.addEventListener('pointermove', this.onMove);
    this.addEventListener('pointerenter', (event) => {
      const rect = this.getBoundingClientRect();
      this.current.x = this.target.x = event.clientX - rect.left;
      this.current.y = this.target.y = event.clientY - rect.top;
    });

    this.rows.forEach((row, index) => {
      row.addEventListener('pointerenter', () => this.show(index));
      row.addEventListener('focus', () => this.show(index));
    });
    this.addEventListener('pointerleave', () => this.hide());
  }

  show(index) {
    this.classList.add('is-previewing');
    this.rows.forEach((row, i) => row.classList.toggle('is-active', i === index));
    this.images.forEach((image, i) => image.classList.toggle('is-active', i === index));
  }

  hide() {
    this.classList.remove('is-previewing');
    this.rows.forEach((row) => row.classList.remove('is-active'));
  }
}

/* ---------- Lookbook: vertical scroll drives a horizontal strip ---------- */

class DuhnLookbook extends HTMLElement {
  connectedCallback() {
    this.strip = this.querySelector('[data-strip]');
    this.counter = this.querySelector('[data-counter]');
    this.items = this.strip ? this.strip.children.length : 0;
    this.media = window.matchMedia('(min-width: 990px)');
    this.layout = () => {
      if (!this.strip) return;
      const enabled = this.media.matches && !reduceMotion.matches;
      this.classList.toggle('is-pinned', enabled);
      if (!enabled) {
        this.style.height = '';
        this.strip.style.transform = '';
        return;
      }
      this.distance = Math.max(0, this.strip.scrollWidth - window.innerWidth);
      this.style.height = `${this.distance + window.innerHeight}px`;
      this.update();
    };
    this.update = () => {
      if (!this.classList.contains('is-pinned')) return;
      const rect = this.getBoundingClientRect();
      const progress = clamp(-rect.top / Math.max(1, this.distance), 0, 1);
      this.strip.style.transform = `translate3d(${-progress * this.distance}px, 0, 0)`;
      this.style.setProperty('--progress', progress.toFixed(4));
      if (this.counter) {
        const index = Math.min(this.items, Math.floor(progress * this.items) + 1);
        this.counter.textContent = String(index).padStart(2, '0');
      }
    };
    this.resizeObserver = new ResizeObserver(this.layout);
    this.resizeObserver.observe(this.strip);
    this.media.addEventListener('change', this.layout);
    scrollSubscribers.add(this.update);
    this.layout();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.media?.removeEventListener('change', this.layout);
    scrollSubscribers.delete(this.update);
  }
}

/* ---------- Countdown ---------- */

class DuhnCountdown extends HTMLElement {
  connectedCallback() {
    const raw = (this.dataset.date || '').trim().replace(' ', 'T');
    this.target = new Date(raw).getTime();
    this.parts = {
      d: this.querySelector('[data-d]'),
      h: this.querySelector('[data-h]'),
      m: this.querySelector('[data-m]'),
      s: this.querySelector('[data-s]'),
    };
    if (Number.isNaN(this.target)) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const diff = Math.max(0, this.target - Date.now());
    const values = {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff / 3600000) % 24),
      m: Math.floor((diff / 60000) % 60),
      s: Math.floor((diff / 1000) % 60),
    };
    for (const [key, el] of Object.entries(this.parts)) {
      if (el) el.textContent = String(values[key]).padStart(2, '0');
    }
    if (diff === 0) {
      clearInterval(this.timer);
      this.closest('.duhn-drop')?.classList.add('is-live');
      this.classList.add('is-ended');
    }
  }
}

/* ---------- Hotspots (shop the look) ---------- */

class DuhnHotspots extends HTMLElement {
  connectedCallback() {
    this.pins = [...this.querySelectorAll('[data-pin]')];
    this.items = [...this.querySelectorAll('[data-pin-item]')];
    const activate = (index) => {
      this.pins.forEach((pin, i) => pin.classList.toggle('is-active', i === index));
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
    };
    this.pins.forEach((pin, i) => {
      pin.addEventListener('pointerenter', () => activate(i));
      pin.addEventListener('focus', () => activate(i));
      pin.addEventListener('click', () => activate(i));
    });
    this.items.forEach((item, i) => item.addEventListener('pointerenter', () => activate(i)));
    activate(0);
  }
}

[
  ['duhn-carousel', DuhnCarousel],
  ['duhn-hero', DuhnHero],
  ['duhn-manifesto', DuhnManifesto],
  ['duhn-index', DuhnIndex],
  ['duhn-lookbook', DuhnLookbook],
  ['duhn-countdown', DuhnCountdown],
  ['duhn-hotspots', DuhnHotspots],
].forEach(([name, ctor]) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
});

/* ---------- Reveal on scroll ---------- */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      )
    : null;

function observeReveals(root = document) {
  root.querySelectorAll('.duhn [data-reveal]:not(.is-in)').forEach((el) => {
    if (revealObserver) revealObserver.observe(el);
    else el.classList.add('is-in');
  });
}

function init3dSections() {
  if (reduceMotion.matches || document.documentElement.classList.contains('shopify-design-mode')) return;
  const sections = [...document.querySelectorAll('#MainContent > .shopify-section')].slice(1)
    .filter((el) => !el.querySelector('duhn-lookbook, .duhn-sr3d, duhn-hero, .duhn-camp'));
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('duhn-3d-in');
      io.unobserve(entry.target);
      setTimeout(() => entry.target.classList.add('duhn-3d-done'), 1300);
    }
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0.02 });
  const below = sections.filter((el) => el.getBoundingClientRect().top >= window.innerHeight);
  below.forEach((el) => { el.classList.add('duhn-3d'); io.observe(el); });
}
init3dSections();

/* Pause looping animations while they are off screen. */
if ('IntersectionObserver' in window) {
  const pauser = new IntersectionObserver((entries) => {
    for (const e of entries) e.target.classList.toggle('is-paused', !e.isIntersecting);
  });
  document.querySelectorAll('.duhn-marquee, .duhn-sale').forEach((el) => pauser.observe(el));
}

window.duhnReady = true;
observeReveals();
document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));

/* ---------- Custom cursor ---------- */

function initCursor() {
  if (!finePointer.matches || !document.body.dataset.duhnCursor) return;
  const cursor = document.createElement('div');
  cursor.className = 'duhn-cursor';
  cursor.setAttribute('aria-hidden', 'true');
  document.body.append(cursor);

  document.addEventListener(
    'pointermove',
    (event) => {
      cursor.style.setProperty('--x', `${event.clientX}px`);
      cursor.style.setProperty('--y', `${event.clientY}px`);
      const target = event.target instanceof Element ? event.target.closest('[data-cursor]') : null;
      const blocked = event.target instanceof Element && event.target.closest('button, form, .duhn-card__quick');
      if (target && !blocked) {
        cursor.textContent = target.dataset.cursor;
        cursor.classList.toggle('duhn-cursor--light', target.dataset.cursorTone === 'light');
        cursor.classList.add('is-active');
      } else {
        cursor.classList.remove('is-active');
      }
    },
    { passive: true }
  );
  document.addEventListener('pointerleave', () => cursor.classList.remove('is-active'));
  // Content moves under a still pointer while scrolling, so hide until the next move.
  scrollSubscribers.add(() => cursor.classList.remove('is-active'));
}

initCursor();

/* ---------- 3D tilt on product cards ---------- */

function initTilt() {
  if (!finePointer.matches || reduceMotion.matches) return;
  document.addEventListener('pointermove', (event) => {
    const media = event.target instanceof Element ? event.target.closest('.duhn-card__media, .card-gallery, .product-media') : null;
    document.querySelectorAll('.is-tilting').forEach((el) => {
      if (el !== media) { el.classList.remove('is-tilting'); el.style.removeProperty('--rx'); el.style.removeProperty('--ry'); }
    });
    if (!media) return;
    const r = media.getBoundingClientRect();
    const x = (event.clientX - r.left) / r.width - 0.5;
    const y = (event.clientY - r.top) / r.height - 0.5;
    media.classList.add('is-tilting');
    const k = media.classList.contains('product-media') ? 0.45 : 1;
    media.style.setProperty('--rx', `${(-y * 10 * k).toFixed(2)}deg`);
    media.style.setProperty('--ry', `${(x * 12 * k).toFixed(2)}deg`);
    media.style.setProperty('--gx', `${((x + 0.5) * 100).toFixed(1)}%`);
    media.style.setProperty('--gy', `${((y + 0.5) * 100).toFixed(1)}%`);
  }, { passive: true });
}

initTilt();

/* ---------- Quick add (sizes) via the Cart API ---------- */

let toastTimer;
function toast(message, { error = false } = {}) {
  let el = document.querySelector('.duhn-toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'duhn-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.append(el);
  }
  el.classList.toggle('duhn-toast--error', error);
  el.innerHTML = '';
  const text = document.createElement('span');
  text.textContent = message;
  el.append(text);
  if (!error) {
    const link = document.createElement('a');
    link.href = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : '/cart';
    link.textContent = 'View bag';
    el.append(link);
  }
  requestAnimationFrame(() => el.classList.add('is-visible'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 4200);
}

async function refreshCartBubble() {
  try {
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
    document.querySelectorAll('.cart-bubble__text-count, [data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
    });
    document.querySelectorAll('.cart-bubble').forEach((el) => el.classList.toggle('visually-hidden', cart.item_count === 0));
  } catch (_) {
    /* Non-critical: the bubble refreshes on next page view. */
  }
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-duhn-quick-add]')) return;
  event.preventDefault();
  const button = form.querySelector('button');
  button?.classList.add('is-loading');
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Could not add to bag');
    toast(`${data.product_title}${data.variant_title ? `, ${data.variant_title}` : ''} added`);
    refreshCartBubble();
  } catch (error) {
    toast(error.message, { error: true });
  } finally {
    button?.classList.remove('is-loading');
  }
});
